import {
    GelForm,
    GelButton,
    GelContainerLite
} from "@tal-gel/components";
import { getGelTokens } from "@tal-gel/theming";
import { Fragment, useContext, useEffect, useState } from "react";
import { Activities, MemberClaims, AssociatedDocuments, DocumentsHistory } from "../../../common/models/memberclaims";
import { FormatHelper } from '../../../common/formatHelper.common';
import { ACTIVITY_TYPE, AdobeClickType, CLAIM_STATUS_TYPE, LMC } from "../../../constants/constants";
import styled from "@emotion/styled";
import ClaimHistoryItem from "./claimHistory-item";
import { AdobeAnalytics } from "../../../common/analytics/adobe-analytics";
import { useCookies } from "react-cookie";
import { useGetClaimDetailsContent } from "../../../common/api/graphQLDataFetch";
import { UserContext } from "../../../common/usercontext/user.context";
import { useActiveUploadStore } from "../documentsupload/activeUploadStore";
import { interpolateString } from "../../../common/helpers/helpers";

interface ClaimHistoryProps {
    claimDetails: MemberClaims
}

const { global: { sizeBaseUnit, themeColorBorderDefault, themeColorGrayT05, sizeBaseX2, themeColorBackgroundDefault } } = getGelTokens();

const Footer = styled.div(() => ({
    height: sizeBaseX2 * 10,
    color: themeColorBackgroundDefault,
    paddingTop: sizeBaseX2
}));

const ClaimHistory = (props: ClaimHistoryProps) => {
    const [{ tpid }] = useCookies(["tpid"])
    const { contextData } = useContext(UserContext);
    const {
        claimDetails        
    } = props;

    const [rowCount, setRowCount] = useState(0);
    const [claimActivityDetailsPage, setclaimActivityDetailsPage] = useState(new Array<Activities>());
    const [claimActivityDetailsFull, setclaimActivityDetailsFull] = useState(new Array<Activities>());
    const uploadCompletedAt = useActiveUploadStore(s => s.completedAt);
    
    const { data } = useGetClaimDetailsContent()

    const LoadNextPage = () => {

        if (rowCount <= claimActivityDetailsFull.length) {
            setRowCount(rowCount + 5);
            setclaimActivityDetailsPage(claimActivityDetailsFull.slice(0, rowCount));
        }

        AdobeAnalytics.PushClickEvent(tpid, data?.additionalDocuments?.HistoryLoadMoreButtonText!, AdobeClickType.Button)
    }

    const setClaimHistoryRecords = () => {
        let claimActivityDetails = new Array<Activities>();
        claimDetails?.activities?.filter(ac => !ac.hideActivity).forEach((activity) => {

            if (activity.type === ACTIVITY_TYPE.REQUIREMENT) {

                /* logic for creating a duplicate activity in runtime for those requiments which are completed. ex: required document uploaded. */
                const activityDetails: Activities = {
                    claimPaymentId: 0,
                    hideActivity: false,
                    title: data?.requirement?.RequirementRaisedText!,
                    message: FormatHelper.stringFormat(data?.requirement?.RequirementRaisedMessage!, activity.friendlyText),
                    description: activity.description,
                    type: activity.type,
                    date: activity.date,
                    friendlyText: activity.friendlyText,
                    documents: new Array<AssociatedDocuments>()
                };

                claimActivityDetails.push(activityDetails);
            }
            else if (activity.type === ACTIVITY_TYPE.PAYMENT) {
                activity.hideActivity = false;
                activity.title = data?.payment?.PaymentText!;
                activity.message = FormatHelper.stringFormat(data?.payment?.PaymentMessage!,
                    activity.friendlyText, FormatHelper.formatDate(activity.date));
                claimActivityDetails.push(activity);
            }
            else if (activity.type === ACTIVITY_TYPE.ACTIVITY) {
                if (activity.title.toLocaleLowerCase().includes(CLAIM_STATUS_TYPE.RTS.toLocaleLowerCase())) {
                    activity.message = `${data?.claimHistoryStatus?.ClaimHistoryRefferedToDescription} ${FormatHelper.formatDate(activity.date)}`;
                }
                else if (activity.title.toLocaleLowerCase().includes(CLAIM_STATUS_TYPE.CLG.toLocaleLowerCase())) {
                    activity.documents = contextData?.otherDocuments?.filter(doc =>
                        doc.createdBy.toLowerCase().includes(LMC.toLowerCase())
                        && doc.claimId === claimDetails.claimId) || [];

                    const message = `${data?.claimHistoryStatus?.ClaimHistoryStartedDescription} ${FormatHelper.formatDate(activity.date)}`;

                    activity.message = activity.documents.length
                        ? `${data?.claimHistoryStatus?.ClaimLodgementDescription} <br /> ${message}`
                        : message;
                                     
                }
                else if (activity.title.toLocaleLowerCase().includes(CLAIM_STATUS_TYPE.CL.toLocaleLowerCase())) {
                    activity.message = `${'Claim closed on'} ${FormatHelper.formatDate(activity.date)}`;
                }
                else if (activity.title.toLocaleLowerCase().includes(CLAIM_STATUS_TYPE.RO.toLocaleLowerCase())) {
                    activity.message = `${'Claim re-opened on'} ${FormatHelper.formatDate(activity.date)}`;
                }
                else if (activity.title.toLocaleLowerCase().includes(CLAIM_STATUS_TYPE.APPROVED.toLocaleLowerCase())) {
                    activity.message = `${data?.claimHistoryStatus?.ClaimHistoryApprovedDescription} ${FormatHelper.formatDate(activity.date)}`;
                }

                activity.hideActivity = false;
                claimActivityDetails.push(activity);
            }
        })

        // Create history item for documents uploaded outside of requirements grouped by day
        if (contextData?.otherDocuments?.length) {
            Object.entries(
                contextData.otherDocuments
                    .filter(doc => doc.claimId === claimDetails.claimId && !doc.createdBy.toLowerCase().includes(LMC.toLowerCase()))
                    .reduce<Record<string, DocumentsHistory[]>>(
                        (acc, val) => ((acc[val.documentUploadDate] ??= []).push(val), acc),
                        {}
                    ))
                .map(([day, documents]: [string, DocumentsHistory[]]): Activities => ({
                    claimPaymentId: 0,
                    hideActivity: false,
                    title: data?.claimHistoryStatus?.ClaimHistoryUploadedDocuments?.AdditionalDocuments?.Header || '',
                    message: data?.claimHistoryStatus?.ClaimHistoryUploadedDocuments?.AdditionalDocuments?.Description || '',
                    description: '',
                    type: ACTIVITY_TYPE.REQUIREMENT,
                    date: day,
                    friendlyText: '',
                    documents: documents.map(d => ({
                        documentName: d.documentName,
                        displayName: d.displayName,
                        documentUploadDate: d.documentUploadDate,
                        sharePointDocumentIdentifier: d.sharePointDocumentIdentifier
                    } as AssociatedDocuments))
                }))
                .forEach(dailyActivity => claimActivityDetails.push(dailyActivity));
        }

        // Create history items for documents associated with requirements grouped by day
        contextData.claims
        .filter(c => c.claimId === claimDetails.claimId)
        .flatMap(c => c.requirements ?? [])
        .forEach(req => {
            Object.entries(
                (req.associatedDocuments ?? []).reduce<Record<string, AssociatedDocuments[]>>(
                    (acc, val) => ((acc[val.documentUploadDate] ??= []).push(val), acc),
                        {}
                    )).map(([day, documents]: [string, AssociatedDocuments[]]): Activities => ({
                        claimPaymentId: 0,
                        hideActivity: false,
                        title: data?.claimHistoryStatus?.ClaimHistoryUploadedDocuments?.Requirement?.Header || '',
                        message: interpolateString(data?.claimHistoryStatus?.ClaimHistoryUploadedDocuments?.Requirement?.Description, req.friendlyText),
                        description: '',
                        type: ACTIVITY_TYPE.REQUIREMENT,
                        date: day,
                        friendlyText: req.friendlyText,
                        documents: documents
                    } as Activities)).forEach(dailyActivity => claimActivityDetails.push(dailyActivity));
            })

        claimActivityDetails.sort((a, b) => FormatHelper.toDate(b.date).getTime() - FormatHelper.toDate(a.date).getTime());
        setclaimActivityDetailsFull(claimActivityDetails);
    }

    // Recompute claim history whenever claimDetails or data changes
    useEffect(() => {
        if (!claimDetails || !data) return;

        setClaimHistoryRecords();
        setRowCount(5);
    }, [claimDetails, data, contextData, uploadCompletedAt]);

    // Slice the full list whenever rowCount or full list changes
    useEffect(() => {
        setclaimActivityDetailsPage(claimActivityDetailsFull.slice(0, rowCount));
    }, [rowCount, claimActivityDetailsFull]);

    return (
        <div style={{ paddingTop: sizeBaseUnit * 4, paddingBottom: sizeBaseUnit * 5 }}>
            <GelContainerLite style={{
                borderBottom: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                borderTop: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                borderLeft: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                borderRight: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                width: "auto",
                cursor: "pointer",
                marginLeft: sizeBaseUnit * 6,
                marginRight: sizeBaseUnit * 6
            }}>
                <div style={{ maxHeight: "500px", overflowY: "auto", overflowX: "hidden" }}>
                    {claimActivityDetailsPage.map((activity, idx) =>
                        <Fragment key={idx}>
                            <ClaimHistoryItem activity={activity} marginNeeded={rowCount > 5} />
                            <div style={{ border: `${sizeBaseUnit / 4}px solid ${themeColorGrayT05}` }} />
                        </Fragment>
                    )}
                </div>

                {rowCount < claimActivityDetailsFull.length &&
                    <Footer>
                        <GelForm onSubmit={LoadNextPage}>
                            <GelButton style={{ width: "100%" }} primary large submit>
                                {data?.additionalDocuments?.HistoryLoadMoreButtonText}
                            </GelButton>
                        </GelForm>
                    </Footer>}
            </GelContainerLite>
        </div>
    );
};

export default ClaimHistory;
